<template>
  <div
    v-if="type !== 'button'"
    :class="['w-fit', rootExtraClasses ? rootExtraClasses.split(' ') : null]"
    :data-testid="testId ?? undefined"
  >
    <input
      v-if="type === 'radio'"
      :id="id"
      :checked="checked"
      :disabled
      type="radio"
      :name="radioGroupName"
      :title="discernibleText"
      class="[&:checked+label]:halebop-focus-rounded"
      @click="
        (e: any) => emit('onClicked', { value: e.target.checked, filterId })
      "
    />
    <input
      v-if="type === 'checkbox'"
      :id="id"
      :checked="checked"
      :disabled
      :aria-disabled="disabled"
      type="checkbox"
      :title="discernibleText"
      class="[&:checked+label]:halebop-focus-rounded"
      @click="
        (e: any) => emit('onClicked', { value: e.target.checked, filterId })
      "
    />
    <label
      :class="`${classList} ${labelClass} ${bgColor == 'checked' ? 'bg-surface-action-1 text-text-on-highlight' : 'bg-surface-action-2 text-text-on-action'}`"
      :for="id"
    >
      <span class="flex gap-8">
        <slot name="iconBefore" />
        <slot name="label" />
        <slot class="inline-block" name="iconAfter" /> </span
    ></label>
  </div>
  <button
    v-if="type === 'button'"
    :aria-label="discernibleText"
    :class="`${classList} ${labelClass} ${bgColor == 'checked' ? 'bg-surface-action-1 text-text-on-highlight' : 'bg-surface-action-2 text-text-on-action'}`"
    :data-testid="testId ?? undefined"
    @click="() => emit('onClicked')"
  >
    <span class="flex gap-8">
      <slot name="iconBefore" />
      <slot name="label" />
      <slot class="inline-block" name="iconAfter" />
    </span>
  </button>
</template>

<script lang="ts" setup>
import type { FilterGroupType } from '~/models/Filter.models';

const emit = defineEmits(['onClicked']);
const id = useId();

withDefaults(
  defineProps<{
    filterId?: string;
    type?: FilterGroupType | 'button';
    checked?: boolean;
    disabled?: boolean;
    radioGroupName?: string;
    discernibleText?: string;
    bgColor?: string;
    rootExtraClasses?: string;
    classList?: string;
    testId?: string;
  }>(),
  {
    filterId: '',
    type: 'checkbox',
    checked: false,
    radioGroupName: '',
    discernibleText: '',
    bgColor: '',
    rootExtraClasses: '',
    classList: undefined,
    testId: undefined,
  },
);

const labelClass = `
  h-44
  flex
  justify-center
  items-center
  px-16
  heading-xxs
  text-text-on-action
  rounded-full
  align-center
  cursor-pointer
  border-border-primary
  border-md
  border-solid
  shadow-button
  whitespace-nowrap
  hover:bg-surface-action-hover
  hover:text-text-on-action
  active:bg-surface-secondary
  active:text-text-on-highlight
`;
</script>

<style scoped lang="scss">
input {
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 0.1rem;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
  &:focus + label {
    @apply outline outline-2 outline-border-highlight outline-offset-input;
  }
  &:checked {
    + label {
      background-color: var(--surface-action-1);
      border: 2px solid var(--border-primary);
      box-shadow: 1px 1px 0 var(--btm-hard-shadow);
      * {
        color: var(--text-on-highlight) !important;
      }
    }
  }
}
</style>
